import { API_BASE, API_KEY } from './config';

async function fetchJson<T>(url: string): Promise<T> {
  const separator = url.includes('?') ? '&' : '?';
  const urlWithKey = API_KEY ? `${API_BASE}${url}${separator}code=${API_KEY}` : `${API_BASE}${url}`;
  const token = localStorage.getItem('auth_token');
  const res = await fetch(urlWithKey, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = '';
    try { detail = JSON.parse(body)?.error || body; } catch { detail = body; }
    throw new Error(detail || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

async function postJson<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const separator = url.includes('?') ? '&' : '?';
  const urlWithKey = API_KEY ? `${API_BASE}${url}${separator}code=${API_KEY}` : `${API_BASE}${url}`;
  const token = localStorage.getItem('auth_token');
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 300_000); // 5 min
  try {
    const res = await fetch(urlWithKey, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      let detail = '';
      try { detail = JSON.parse(body)?.error || body; } catch { detail = body; }
      throw new Error(detail || `${res.status} ${res.statusText}`);
    }
    const text = await res.text();
    if (!text) return {} as T;
    return JSON.parse(text);
  } finally {
    clearTimeout(timeout);
  }
}

export interface SymbolStatus {
  symbol: string;
  paused: boolean;
}

export interface Prediction {
  symbol: string;
  timestamp: number;
  timeframe: string;
  date: string;
  recommendation: string;
  predictedQuote: number;
  positiveSentiments: number;
  negativeSentiments: number;
  neutralSentiments: number;
  reasoning?: string;
  predictionExplanation?: string;
  evidenceCount: number;
}

export interface NewsItem {
  id: string;
  symbol: string;
  summary: string;
  sentiment: string;
  timestamp: number;
  date: string;
  source: string;
  url?: string;
  data?: string;
  category?: string;
}

export interface TrendItem {
  symbol: string;
  timestamp: number;
  period: string;
  buy: number;
  hold: number;
  sell: number;
  strongBuy: number;
  strongSell: number;
}

export interface PressRelease {
  id: string;
  symbol: string;
  summary?: string;
  title?: string;
  sentiment: string;
  timestamp: number;
  date: string;
  data?: string;
}

export interface Quote {
  symbol: string;
  timestamp: number;
  date: string;
  currentPrice: number;
  change: number;
  percentChange: number;
  highPriceDay: number;
  lowPriceDay: number;
  openPrice?: number;
  previousClosePrice?: number;
}

export interface EarningsDate {
  symbol: string;
  date: string | null;
  daysUntil: number | null;
  quarter: number | null;
  year: number | null;
  reportType: 'quarterly' | 'annual' | null;
  epsEstimate: number | null;
  revenueEstimate: number | null;
  lastClosePrice: number | null;
  lastOpenPrice: number | null;
  lastPercentChange: number | null;
}

export interface FinancialConcept {
  label: string;
  value: number;
  unit: string;
}

export interface FinancialReport {
  symbol: string;
  cik: string | null;
  year: number;
  quarter: number;
  form: string;
  startDate: string;
  endDate: string;
  filedDate: string;
  acceptedDate: string;
  accessNumber: string;
  incomeStatement: FinancialConcept[];
  balanceSheet: FinancialConcept[];
  cashFlow: FinancialConcept[];
}

export interface EarningsPredictionDay {
  pctChange: number;
  predictedClose: number;
}

export interface EarningsPrediction {
  symbol: string;
  lastClose: number;
  recentPrices: { date: string; close: number; pctChange: number }[];
  generatedAt: string;
  model: string;
  patternsAnalyzed: number;
  confidence: 'Low' | 'Medium' | 'High';
  overallSummary: string;
  matchedPattern: {
    label: string;
    similarity: string;
    historicalOutcome: string;
  };
  patternPrediction: {
    day1: EarningsPredictionDay;
    day2: EarningsPredictionDay;
    day3: EarningsPredictionDay;
    day4: EarningsPredictionDay;
    day5: EarningsPredictionDay;
  };
  newsAdjustedPrediction: {
    day1: EarningsPredictionDay;
    day2: EarningsPredictionDay;
    day3: EarningsPredictionDay;
    day4: EarningsPredictionDay;
    day5: EarningsPredictionDay;
  };
  newsAdjustmentReasoning: string;
}

export interface XGBoostPrediction {
  symbol: string;
  lastClose: number;
  latestDate: string;
  recentPrices: { date: string; close: number; pctChange: number }[];
  horizon: number;
  prediction: {
    direction: 'up' | 'down';
    predictedReturn: number;
    predictedClose: number;
    confidenceScore: number;
    confidence: 'Low' | 'Medium' | 'High';
  };
  forecast: { day: number; pctChange: number; predictedClose: number; dayConfidence: number }[];
  fiveDayForecast: {
    day1: { pctChange: number; predictedClose: number; dayConfidence: number };
    day2: { pctChange: number; predictedClose: number; dayConfidence: number };
    day3: { pctChange: number; predictedClose: number; dayConfidence: number };
    day4: { pctChange: number; predictedClose: number; dayConfidence: number };
    day5: { pctChange: number; predictedClose: number; dayConfidence: number };
  };
  modelMetrics: {
    cvAccuracy: number;
    cvFolds: number;
    trainingRows: number;
    features: number;
    horizon?: number;
    threshold?: number;
  };
  topFeatures: { feature: string; importance: number }[];
  generatedAt: string;
  model: string;
}

export interface EarningsQuoteDay {
  date: string;
  close: number;
  open: number;
  high: number;
  low: number;
  change: number;
  percentChange: number;
  isReportDay: boolean;
}

export interface EarningsQuoteReport {
  symbol: string;
  year: number;
  quarter: number;
  filedDate: string;
  form: string;
  freq: string;
  label: string;
  startDate: string;
  endDate: string;
  quotes: EarningsQuoteDay[];
}

export interface StoredPrediction {
  id: string;
  symbol: string;
  date: string;
  timestamp: number;
  timeframe: string;
  model: string;
  recommendation: string;
  predictedQuote: number;
  prediction: {
    direction: 'up' | 'down';
    predictedReturn: number;
    predictedClose: number;
    confidenceScore: number;
    confidence: string;
  };
  forecast?: { day: number; pctChange: number; predictedClose: number; dayConfidence: number }[];
  fiveDayForecast?: Record<string, { pctChange: number; predictedClose: number; dayConfidence?: number }>;
  horizon?: number;
  lastClose?: number;
  latestDate?: string;
  modelMetrics?: {
    cvAccuracy: number;
    cvFolds: number;
    trainingRows: number;
    features: number;
  };
  reasoning?: string;
  predictionExplanation?: string;
}

export interface CompanyProfile {
  symbol: string;
  name?: string;
  ticker?: string;
  exchange?: string;
  currency?: string;
  country?: string;
  finnhubIndustry?: string;
  ipo?: string;
  logo?: string;
  weburl?: string;
  /** Market capitalization in millions of USD (as returned by Finnhub). */
  marketCapitalization?: number;
  shareOutstanding?: number;
  error?: string;
}

export const api = {
  getSymbols: () => fetchJson<string[]>('/symbols'),
  getPredictions: (symbol?: string, timeframe?: string) => {
    const params = new URLSearchParams();
    if (symbol) params.set('symbol', symbol);
    if (timeframe) params.set('timeframe', timeframe);
    return fetchJson<Prediction[]>(`/predictions?${params}`);
  },
  getPredictionHistory: (symbol: string, model?: string) => {
    const params = new URLSearchParams({ symbol });
    if (model) params.set('model', model);
    return fetchJson<StoredPrediction[]>(`/predictions?${params}`);
  },
  getNews: (symbol?: string, period?: string) => {
    const params = new URLSearchParams();
    if (symbol) params.set('symbol', symbol);
    if (period) params.set('period', period);
    return fetchJson<NewsItem[]>(`/news?${params}`);
  },
  getTrends: (symbol?: string, period?: string) => {
    const params = new URLSearchParams();
    if (symbol) params.set('symbol', symbol);
    if (period) params.set('period', period);
    return fetchJson<TrendItem[]>(`/trends?${params}`);
  },
  getPressReleases: (symbol?: string, period?: string) => {
    const params = new URLSearchParams();
    if (symbol) params.set('symbol', symbol);
    if (period) params.set('period', period);
    return fetchJson<PressRelease[]>(`/press-releases?${params}`);
  },
  getQuotes: (symbol: string, days?: number) => {
    const params = new URLSearchParams({ symbol });
    if (days) params.set('days', String(days));
    return fetchJson<Quote[]>(`/quotes?${params}`);
  },
  getProfile: (symbol: string) => {
    const params = new URLSearchParams({ symbol });
    return fetchJson<CompanyProfile>(`/profile?${params}`);
  },
  getProfiles: (symbols: string[]) => {
    const params = new URLSearchParams({ symbols: symbols.join(',') });
    return fetchJson<CompanyProfile[]>(`/profile?${params}`);
  },
  getEarnings: (symbol?: string) => {
    const params = new URLSearchParams();
    if (symbol) params.set('symbol', symbol);
    return fetchJson<EarningsDate[]>(`/earnings?${params}`);
  },
  getFinancialReports: (symbol: string, freq?: string) => {
    const params = new URLSearchParams({ symbol });
    if (freq) params.set('freq', freq);
    return fetchJson<FinancialReport[]>(`/financial-reports?${params}`);
  },
  getEarningsQuotes: (symbol: string, year?: number, quarter?: number) => {
    const params = new URLSearchParams({ symbol });
    if (year) params.set('year', String(year));
    if (quarter) params.set('quarter', String(quarter));
    return fetchJson<EarningsQuoteReport[]>(`/earnings-quotes?${params}`);
  },

  // Stock management
  addSymbol: (symbol: string) =>
    postJson<{ symbols: string[]; message: string }>('/manage-stocks/add', { symbol }),
  removeSymbol: (symbol: string) =>
    postJson<{ symbols: string[]; message: string }>('/manage-stocks/remove', { symbol }),
  // Pause/resume data collection for a single symbol (news, quotes, trends,
  // predictions, etc). Existing data is preserved.
  setSymbolPaused: (symbol: string, paused: boolean) =>
    postJson<{ status: SymbolStatus[]; message: string }>('/manage-stocks/pause', { symbol, paused }),
  getSymbolsStatus: () =>
    fetchJson<SymbolStatus[]>('/manage-stocks/status'),

  // Backfill endpoints (return after each step completes)
  backfillNews: (symbol: string, date: string) =>
    postJson<{ status: string; step: string }>('/backfill/news', { symbol, date }),
  backfillTrends: (symbol: string) =>
    postJson<{ status: string; step: string }>('/backfill/trends', { symbol }),
  backfillQuotes: (symbol: string, date?: string) =>
    postJson<{ status: string; step: string }>('/backfill/quotes', { symbol, ...(date && { date }) }),
  backfillPressReleases: (symbol: string) =>
    postJson<{ status: string; step: string }>('/backfill/press-releases', { symbol }),
  backfillFinancialReports: (symbol: string) =>
    postJson<{ status: string; step: string }>('/backfill/financial-reports', { symbol }),
  backfillPredictions: (symbol: string) =>
    postJson<{ status: string; step: string; results?: Record<string, string> }>('/backfill/predictions', { symbol }),
  predictEarningsImpact: (symbol: string) =>
    postJson<EarningsPrediction>('/generate-earning-predictions', { symbol }),
  predictXGBoost: (symbol: string, horizon: number = 5) =>
    postJson<XGBoostPrediction>('/generate-xgboost-predictions', { symbol, horizon }),
  predictRandomForest: (symbol: string, horizon: number = 5) =>
    postJson<XGBoostPrediction>('/generate-rf-predictions', { symbol, horizon }),
};

// ─── Portfolio types ─────────────────────────────────────────────────
export interface PortfolioHolding {
  symbol: string;
  quantity: number;
  buyPrice: number;
  goalPrice?: number;
  addedAt: string;
  soldQuantity?: number;
  soldPrice?: number;
  soldAt?: string;
}
